<form class="ppf" [formGroup]="form" (ngSubmit)="save()" novalidate>
  <div class="ppf__layout">
    <div class="ppf__fields">
      <!-- Alias: nunca el nombre real (B6); en minúsculas mientras se escribe. -->
      <div class="ppf__field">
        <label class="ppf__label" for="ppf-alias">{{ copy.aliasLabel }}</label>
        <input
          id="ppf-alias"
          class="ppf__input"
          type="text"
          formControlName="alias"
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          maxlength="20"
          aria-describedby="ppf-alias-help ppf-alias-error"
          [attr.aria-invalid]="fieldErrors().alias ? true : null"
          (input)="onAliasInput()"
        />
        <p class="ppf__help" id="ppf-alias-help">{{ copy.aliasHelp }}</p>
        @if (fieldErrors().alias; as error) {
          <p class="ppf__error" id="ppf-alias-error" data-field="alias">{{ error }}</p>
        }
      </div>

      <!-- Zona: provincia obligatoria + cantón opcional, por nombre de cr.json; sin distrito (B4). -->
      <div class="ppf__row">
        <div class="ppf__field">
          <label class="ppf__label" for="ppf-province">{{ copy.provinceLabel }}</label>
          <select
            id="ppf-province"
            class="ppf__input"
            formControlName="province"
            [attr.aria-invalid]="fieldErrors().province ? true : null"
            (change)="onProvinceChange()"
          >
            <option value="">{{ copy.provincePlaceholder }}</option>
            @for (name of provinceNames(); track name) {
              <option [value]="name">{{ name }}</option>
            }
          </select>
          @if (fieldErrors().province; as error) {
            <p class="ppf__error" data-field="province">{{ error }}</p>
          }
        </div>

        @if (cantonNames().length > 0) {
          <div class="ppf__field">
            <label class="ppf__label" for="ppf-canton">{{ copy.cantonLabel }}</label>
            <select
              id="ppf-canton"
              class="ppf__input"
              formControlName="canton"
              [attr.aria-invalid]="fieldErrors().canton ? true : null"
            >
              <option value="">{{ copy.cantonNone }}</option>
              @for (name of cantonNames(); track name) {
                <option [value]="name">{{ name }}</option>
              }
            </select>
            @if (fieldErrors().canton; as error) {
              <p class="ppf__error" data-field="canton">{{ error }}</p>
            }
          </div>
        } @else if (fieldErrors().canton; as error) {
          <p class="ppf__error" data-field="canton">{{ error }}</p>
        }
      </div>

      <!-- Juegos por slug (B3). -->
      <fieldset class="ppf__fieldset">
        <legend class="ppf__label">{{ copy.gamesLabel }}</legend>
        <p class="ppf__help">{{ copy.gamesHelp }}</p>
        <div class="ppf__pills">
          @for (game of games(); track game.slug) {
            <button
              type="button"
              class="ppf__pill"
              [class.is-active]="isSelected(game.slug)"
              [attr.aria-pressed]="isSelected(game.slug)"
              [style.--pill-color]="game.accent_color"
              (click)="toggleGame(game.slug)"
            >
              {{ game.name }}
            </button>
          }
        </div>
        @if (fieldErrors().games; as error) {
          <p class="ppf__error" data-field="games">{{ error }}</p>
        }
      </fieldset>

      <!-- Contacto (B29): solo lo ve quien acepta (B8). -->
      <fieldset class="ppf__fieldset">
        <legend class="ppf__label">{{ copy.contactLabel }}</legend>
        <p class="ppf__help">{{ copy.contactHelp }}</p>
        <div class="ppf__radios">
          @for (method of contactMethods; track method) {
            <label class="ppf__radio">
              <input type="radio" formControlName="contact_method" [value]="method" [attr.data-method]="method" />
              <span>{{ copy.contactMethods[method] }}</span>
            </label>
          }
        </div>
        @if (fieldErrors().contact_method; as error) {
          <p class="ppf__error" data-field="contact_method">{{ error }}</p>
        }

        @switch (value().contact_method) {
          @case ('whatsapp') {
            <div class="ppf__field">
              <label class="ppf__label ppf__label--sub" for="ppf-contact-value">{{ copy.contactValueLabel.whatsapp }}</label>
              <input id="ppf-contact-value" class="ppf__input" type="tel" formControlName="contact_value" autocomplete="tel" />
            </div>
          }
          @case ('discord') {
            <div class="ppf__field">
              <label class="ppf__label ppf__label--sub" for="ppf-contact-value">{{ copy.contactValueLabel.discord }}</label>
              <input id="ppf-contact-value" class="ppf__input" type="text" formControlName="contact_value" autocomplete="off" autocapitalize="none" />
            </div>
          }
          @case ('email') {
            <p class="ppf__note" data-contact-note="email">{{ copy.contactEmail(accountEmail()) }}</p>
          }
          @case ('tienda') {
            <p class="ppf__note" data-contact-note="tienda">{{ storeContactText }}</p>
          }
        }
        @if (fieldErrors().contact_value; as error) {
          <p class="ppf__error" data-field="contact_value">{{ error }}</p>
        }
      </fieldset>

      <!-- Presentación: texto plano, ≤ 280. -->
      <div class="ppf__field">
        <label class="ppf__label" for="ppf-bio">{{ copy.bioLabel }}</label>
        <textarea
          id="ppf-bio"
          class="ppf__input ppf__textarea"
          formControlName="bio"
          rows="3"
          [attr.maxlength]="bioMax"
          aria-describedby="ppf-bio-help"
        ></textarea>
        <div class="ppf__help-row">
          <p class="ppf__help" id="ppf-bio-help">{{ copy.bioHelp }}</p>
          <span class="ppf__counter" aria-live="polite">{{ copy.bioCounter(bioCount(), bioMax) }}</span>
        </div>
        @if (fieldErrors().bio; as error) {
          <p class="ppf__error" data-field="bio">{{ error }}</p>
        }
      </div>

      <!-- Interruptores. -->
      <label class="ppf__switch">
        <input type="checkbox" role="switch" formControlName="email_notifications" />
        <span>{{ copy.emailNotificationsLabel }}</span>
      </label>

      <div class="ppf__field">
        <label class="ppf__switch" [class.is-disabled]="!verified()">
          <input id="ppf-looking" type="checkbox" role="switch" formControlName="looking_for_players" />
          <span>{{ copy.lookingLabel }}</span>
        </label>
        <p class="ppf__help">{{ copy.lookingHelp }}</p>
        @if (!verified()) {
          <div class="ppf__unverified">
            <p class="ppf__unverified-text">{{ copy.notVerifiedFallback }}</p>
            <p class="ppf__unverified-text" data-unverified-note>{{ copy.notVerifiedSavesInactive }}</p>
            <app-players-resend-verification />
          </div>
        }
        @if (fieldErrors().looking_for_players; as error) {
          <p class="ppf__error" data-field="looking_for_players">{{ error }}</p>
        }
      </div>

      <!-- Consentimiento (B9, B31): solo si el perfil aún no lo tiene y se activa. -->
      @if (profile()?.consented) {
        <p class="ppf__consented">{{ copy.consentAccepted(profile()?.consent_version ?? '') }}</p>
      } @else if (needsConsent()) {
        <label class="ppf__consent">
          <input type="checkbox" formControlName="consent" />
          <span>
            {{ consentCopy.before }}<a
              [href]="privacyPath + '#' + privacyFragment"
              target="_blank"
              rel="noopener"
              >{{ consentCopy.link }}</a
            >{{ consentCopy.after }}
          </span>
        </label>
      }
      @if (fieldErrors().consent; as error) {
        <p class="ppf__error" data-field="consent">{{ error }}</p>
      }

      @if (generalError(); as error) {
        <p class="ppf__alert" role="alert">{{ error }}</p>
      }
      @if (notice(); as text) {
        <p class="ppf__notice" role="status">{{ text }}</p>
      }

      <div class="ppf__actions">
        <button type="submit" class="ppf__save" [disabled]="!canSave()">{{ copy.save }}</button>
      </div>
    </div>

    <!-- Vista previa (D25): la tarjeta de J1 con los valores del formulario. -->
    <aside class="ppf__preview">
      <h4 class="ppf__preview-title">{{ copy.previewTitle }}</h4>
      <app-player-card [card]="preview()" />
    </aside>
  </div>
</form>
